import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { PromptPreview } from "./PromptPreview";

export const PromptEditor = ({ prompt, onClose, onSaved }) => {
  const isNew = !prompt.id;
  const [form, setForm] = useState({
    name: prompt.name || "",
    description: prompt.description || "",
    prompt: prompt.prompt || "",
    business_types: prompt.business_types || [],
    is_default: !!prompt.is_default,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => set("business_types",
    form.business_types.includes(t) ? form.business_types.filter((x) => x !== t) : [...form.business_types, t]);

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Give the prompt a name");
    if (form.prompt.trim().length < 20) return toast.error("The prompt text is too short");
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/inquiry-prompts", form);
      else await api.put(`/admin/inquiry-prompts/${prompt.id}`, form);
      toast.success(`"${form.name.trim()}" is live for all users`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the prompt");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground";
  const inputCls = "mt-2 bg-background border-border rounded-sm focus-visible:ring-[#007AFF]";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-border rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="prompt-editor">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{isNew ? "New drafting prompt" : `Edit — ${prompt.name}`}</DialogTitle>
          <DialogDescription className="text-xs">Saving pushes this prompt live to every user immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="prompt-name-input"
                placeholder="Method 505 — Retail" className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>Short description (shown to users)</Label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="prompt-description-input"
                placeholder="One line on when to use this method" className={inputCls} />
            </div>
          </div>
          <div>
            <Label className={labelCls}>System prompt</Label>
            <Textarea value={form.prompt} onChange={(e) => set("prompt", e.target.value)} data-testid="prompt-text-input"
              rows={16} className={`${inputCls} font-mono text-xs leading-relaxed resize-y`} />
            <p className="text-[10px] text-muted-foreground font-mono mt-1">{form.prompt.length.toLocaleString()} / 20,000 chars</p>
          </div>
          <div>
            <Label className={labelCls}>Auto-select for business types</Label>
            <div className="flex flex-wrap gap-3 mt-2" data-testid="prompt-types">
              {BUSINESS_TYPES.map((t) => (
                <label key={t} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={form.business_types.includes(t)} onCheckedChange={() => toggleType(t)}
                    data-testid={`prompt-type-${t.replace(/\W+/g, "-").toLowerCase()}`} />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={form.is_default} onCheckedChange={(v) => set("is_default", !!v)} data-testid="prompt-default-checkbox" />
            Make this the default prompt (pre-selected when no business-type match)
          </label>

          <PromptPreview prompt={form.prompt} />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm" data-testid="prompt-cancel-btn">Cancel</Button>
            <Button type="button" onClick={save} disabled={saving} data-testid="prompt-save-btn"
              className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : "Save & push live"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
